<template>
  <SoDialog
    v-model="isShown"
    width="600"
    card-title="Take Card Payment"
    :loading="isLoading"
    @on-close="onClose"
  >
    <SoAlert
      v-if="showAccountHolderAlert"
      type="warning"
      class="mb-2"
      data-testid="card-payment-account-holder-alert"
      >Are you the account holder and the only person required to authorise
      payments from this account?
    </SoAlert>
    <SoForm
      v-show="!paymentSession && !paymentId"
      v-model="isFormValid"
      :autocomplete="false"
      @submit="onContinue"
    >
      <SoTextField
        v-model="form.amountInPounds"
        label="Amount in pounds"
        placeholder="0.00"
        :rules="[rules.required, rules.currency, rules.cardPaymentAmount]"
        type="number"
        required
      />

      <SoTextField
        v-model="form.contactEmail"
        label="Contact email (optional)"
        placeholder="john.doe@example.com"
        type="text"
        :rules="[rules.email]"
      />

      <SoCheckbox
        v-model="isCallRecordingDisabled"
        label="I can confirm that I stopped the call recording"
        class="my-5"
        :rules="[rules.isTruthy]"
        required
      />

      <template #actions="{ validate }">
        <SoAlert
          v-if="submissionErrorMessage && submissionErrorTitle"
          :title="submissionErrorTitle"
          :text="submissionErrorMessage"
          class="mt-6"
          data-testid="card-payment-error"
          type="error"
        />

        <div class="d-flex mt-8">
          <SoButton
            title="Cancel"
            type="secondary"
            data-dd-action-name="motoCancelPayment"
            large
            @click="onClose"
          />
          <SoButton
            :loading="isCreatingSession"
            data-dd-action-name="motoContinueToCardDetails"
            title="Continue to card details"
            type="primary"
            class="ml-auto"
            large
            submit
            @click="validate()"
          />
        </div>
      </template>
    </SoForm>

    <div v-show="showFlowSection" data-testid="make-card-payment-flow-section">
      <div class="text-h6 my-4" data-testid="card-payment-flow-amount">
        Payment amount: {{ formattedAmount }}
      </div>

      <div ref="flowContainer" data-testid="card-payment-flow-container" />

      <div v-if="!flowComponent" data-testid="card-payment-flow-skeleton">
        <SoSkeleton v-for="i in 3" :key="i" height="3rem" class="mb-4" />
      </div>

      <SoAlert
        v-if="flowErrorMessage"
        title="Payment error"
        :text="flowErrorMessage"
        class="mt-6"
        data-testid="card-payment-flow-error"
        type="error"
      />

      <div class="d-flex mt-8">
        <SoButton
          title="Cancel"
          type="secondary"
          data-dd-action-name="motoCancelPayment"
          large
          @click="onClose"
        />
      </div>
    </div>

    <CardPaymentSummary
      v-if="paymentId"
      :payment-id="paymentId"
      :form-data="form"
      poll-until-found
      data-testid="card-payment-summary"
    />
  </SoDialog>
  <SoDialog
    v-model="showCallRecordingConfirmationDialog"
    width="600"
    card-title="Call recording confirmation"
    persistent
  >
    <SoCheckbox
      v-model="confirmExit"
      label="I can confirm that I restarted the call recording"
      class="mb-4"
    />
    <div class="d-flex gap-2 justify-end">
      <SoButton
        title="Confirm"
        type="primary"
        data-dd-action-name="motoConfirmCallRec"
        :disabled="!confirmExit"
        large
        @click="$emit('close')"
      />
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import {
    CheckoutFlowService,
    PaymentSessionResult,
    toFlowPaymentSession,
  } from "@/services/CheckoutFlowService"
  import {
    FlowComponent,
    FlowComponentError,
    HANDLED_FLOW_ERROR_CODES,
  } from "@/types/CheckoutFlow"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import rules from "@/validators/rules"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { IContact } from "@/models/contacts/contact.types"
  import CardPaymentSummary from "../components/CardPaymentSummary.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import { errorReporter } from "@/services/ErrorService"

  export default defineComponent({
    components: {
      SoDialog,
      SoButton,
      SoTextField,
      SoCheckbox,
      SoForm,
      SoSkeleton,
      CardPaymentSummary,
      SoAlert,
    },
    emits: ["update:modelValue", "close"],
    data() {
      return {
        rules,
        primaryAccountContact: useCurrentAccountStore().primaryAccountContact,
        coreId: useCurrentAccountStore().coreId,
        showCallRecordingConfirmationDialog: false,
        confirmExit: false,
        isCallRecordingDisabled: false,
        needsRestartRecordingConfirmation: false,
        isShown: true,
        isFormValid: false,
        isCreatingSession: false,
        submissionErrorTitle: "",
        submissionErrorMessage: "",
        flowErrorMessage: "",
        paymentId: "",
        paymentSession: null as PaymentSessionResult | null,
        flowComponent: undefined as FlowComponent | undefined,
        form: {
          amountInPounds: "" as string,
          contactEmail: "" as string,
        },
      }
    },
    computed: {
      isLoading() {
        return this.coreId.isFetching
      },
      showFlowSection() {
        return !!this.paymentSession && !this.paymentId
      },
      showAccountHolderAlert() {
        return !this.isCallRecordingDisabled
      },
      formattedAmount() {
        return formatMoney({
          amount: Number.parseFloat(this.form.amountInPounds),
        })
      },
    },

    watch: {
      ["primaryAccountContact.data"]: {
        deep: true,
        immediate: true,
        handler(contact?: IContact) {
          if (!contact || contact.isDeceased || contact.isOccupier) return
          this.form.contactEmail = contact.email
        },
      },
      isCallRecordingDisabled(isTicked: boolean) {
        if (isTicked) this.needsRestartRecordingConfirmation = true
      },
    },

    unmounted() {
      CheckoutFlowService.unmount(this.flowComponent)
    },

    methods: {
      async onContinue() {
        if (!this.isFormValid) return

        this.isCreatingSession = true
        this.submissionErrorTitle = ""
        this.submissionErrorMessage = ""
        try {
          const session = await this.createPaymentSession()
          this.paymentSession = session
          await this.initFlow(session)
        } catch (error) {
          this.paymentSession = null
          if (error instanceof Error) {
            this.submissionErrorTitle = "Payment session error"
            this.submissionErrorMessage =
              error.message || "Something went wrong. Please try again."
            errorReporter.report(
              new Error("Create Payment Session Error", { cause: error }),
            )
          }
        } finally {
          this.isCreatingSession = false
        }
      },
      async createPaymentSession(): Promise<PaymentSessionResult> {
        // mutateAsync's return type is erased to unknown by MutationResult;
        // the REST payload is the flat PaymentSessionResponse.
        return (await useCurrentAccountStore()
          .createPaymentSession()
          .mutateAsync({
            contactEmail: this.form.contactEmail,
            amountInPounds: Number.parseFloat(this.form.amountInPounds),
            customerName: this.primaryAccountContact.data?.firstName,
          })) as PaymentSessionResult
      },
      async initFlow(session: PaymentSessionResult) {
        await CheckoutFlowService.init({
          paymentSession: toFlowPaymentSession(session),
          onPaymentCompleted: (paymentId: string) => {
            this.paymentId = paymentId
          },
          onError: (error: FlowComponentError) => {
            // Errors the Flow component already shows the user are suppressed
            // here; only unhandled errors get the banner.
            if (error.code && HANDLED_FLOW_ERROR_CODES.includes(error.code)) {
              return
            }

            this.flowErrorMessage =
              error.message ?? "Something went wrong. Please try again."
            errorReporter.report(
              new Error("Card Payment Flow Error", {
                cause: new Error(`${error.code}: ${error.message}`),
              }),
            )
          },
        })

        const container = this.$refs.flowContainer
        if (!(container instanceof HTMLElement)) {
          throw new TypeError("Flow container is not available")
        }

        this.flowComponent = await CheckoutFlowService.mountFlow(container)
      },
      onClose() {
        if (this.needsRestartRecordingConfirmation) {
          this.showCallRecordingConfirmationDialog = true
        } else {
          this.$emit("close")
        }
      },
    },
  })
</script>
